import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import axios from 'axios';
import { useFolder } from '../contexts/FolderContext';
import DateRangeFilter from './DateRangeFilter';
import {
  stripLatestWeekActuals,
  stripLatestWeekAggregatedActuals,
  isInLatestForecastWeek,
  isActualPending,
  getForecastWindow,
} from '../utils/collatedLatestWeek';
import { filterOptumSkus } from '../utils/optumSkuFilter';
import { filterAllowedItems } from '../utils/allowedItemsFilter';
import {
  COMBO_AGGREGATE_ITEMNAME,
  isComboAggregateItem,
  isComboSourceItem,
  buildComboAggregateStrictRows,
  aggregateDailySalesByDate,
  mgTypeForItem,
  normalizeItemKey,
  sumFinite,
  compareItemDisplayOrder,
  displayItemLabel,
  summarizeComboAggregateMetrics,
} from '../utils/comboAggregate';
import {
  ComposedChart,
  Line,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';

const toProperCase = (str) => {
  if (!str) return '';
  return String(str)
    .split(/[\s_]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const formatDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const day = date.getDate().toString().padStart(2, '0');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[date.getMonth()];
  return `${day}-${month}`;
};

// Holiday model dates to exclude: model_date falls on these (month, day)
const HOLIDAY_MODEL_DATES_ITEM = [
  { month: 8, day: 27 },   // 27 Sep (model_date 20250927)
  { month: 9, day: 18 },   // 18 Oct
  { month: 11, day: 20 },  // 20 Dec
  { month: 11, day: 27 },  // 27 Dec
  { month: 0, day: 10 },   // 10 Jan
];

function isModelDateHolidayWeekItem(modelDateStr) {
  if (modelDateStr == null || modelDateStr === '') return false;
  const s = String(modelDateStr).trim().replace(/-/g, '');
  if (s.length !== 8 || !/^\d{8}$/.test(s)) return false;
  const m = parseInt(s.slice(4, 6), 10) - 1;
  const d = parseInt(s.slice(6, 8), 10);
  return HOLIDAY_MODEL_DATES_ITEM.some(h => m === h.month && d === h.day);
}

/** True if calendar date (YYYY-MM-DD) falls on a holiday day (27 Sep, 18 Oct, 20 Dec, 27 Dec, 10 Jan) */
function isDateHolidayDay(dateStr) {
  if (dateStr == null || dateStr === '') return false;
  const d = new Date(String(dateStr).trim().slice(0, 10));
  if (Number.isNaN(d.getTime())) return false;
  const m = d.getMonth();
  const day = d.getDate();
  return HOLIDAY_MODEL_DATES_ITEM.some(h => m === h.month && day === h.day);
}

/** Lighten a hex color with alpha (0–1). Returns 8-char hex e.g. #66bb6a66 */
function lightenHex(hex, alpha = 0.4) {
  if (!hex || typeof hex !== 'string') return hex;
  const a = Math.round(Math.min(1, Math.max(0, alpha)) * 255).toString(16).padStart(2, '0');
  return hex.length === 7 ? `${hex}${a}` : hex;
}

const formatDateWithWeekday = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const day = date.getDate().toString().padStart(2, '0');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[date.getMonth()];
  const weekday = date.toLocaleDateString('en-US', { weekday: 'long' });
  return `${day}-${month} (${weekday})`;
};

/** Format potential_reason from collated_comparison_aggregated (reasons_pre flags) for display */
const formatAggregatedPotentialReason = (potential_reason) => {
  if (!potential_reason || typeof potential_reason !== 'string') return [];
  const raw = String(potential_reason).trim();
  if (!raw) return [];
  const parts = raw.split(';').map((p) => p.trim()).filter(Boolean);
  const map = {
    Insufficient_History_Flag: 'Insufficient History',
    Sparse_Data_Flag: 'Sparse Data',
    Low_Scale_Demand_Flag: 'Low Scale Demand',
    volatile_demand_flag: 'Volatile Demand'
  };
  return parts.map((p) => map[p] || p);
};

/**
 * Convert potential_reason CSV codes to display text (matches ItemDetail.js formatCurrentWeekAnomalies).
 * Input format: "Drop_Tuesday; Spike_Wednesday; Potential Ops Issue (Minor) - Thursday"
 * Output: array of display strings for tooltip.
 */
const formatPotentialReason = (potential_reason) => {
  if (!potential_reason || typeof potential_reason !== 'string') return [];
  const raw = String(potential_reason).trim();
  if (!raw) return [];
  const reasons = raw.split(';').map((r) => r.trim()).filter(Boolean);
  const reasonsLower = reasons.map((r) => r.toLowerCase());
  const display = [];
  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  dayNames.forEach((day) => {
    const dayLower = day.toLowerCase();
    if (reasonsLower.some((r) => r === `spike_${dayLower}`)) {
      display.push(`Spike over Recent ${day}s`);
    }
    if (reasonsLower.some((r) => r === `drop_${dayLower}`)) {
      display.push(`Drop over Recent ${day}s`);
    }
  });
  dayNames.forEach((day) => {
    const dayLower = day.toLowerCase();
    if (reasons.some((r) => {
      const rLower = r.toLowerCase();
      return rLower.includes(`potential ops issue (minor) - ${dayLower}`) || rLower.includes(`potential minor issue - ${dayLower}`);
    })) {
      display.push(`Potential Ops Issue (Minor) - ${day}`);
    } else if (reasons.some((r) => {
      const rLower = r.toLowerCase();
      return (rLower.includes(`potential ops issue (major) - ${dayLower}`));
    })) {
      display.push(`Potential Ops Issue (Major) - ${day}`);
    } else if (reasons.some((r) => {
      const rLower = r.toLowerCase();
      return rLower.includes(`potential ops issue - ${dayLower}`) && !rLower.includes('minor') && !rLower.includes('major');
    })) {
      display.push(`Potential Ops Issue - ${day}`);
    }
  });
  if (reasonsLower.includes('post_spike_flag')) {
    display.push('Overall Spike in Sales Across the Week');
  }
  if (reasonsLower.includes('post_drop_flag')) {
    display.push('Overall Drop in Sales Across the Week');
  }
  return display;
};

/** Shared axis tick style so Daily Level Accuracy and Session Slot-Wise Distribution align visually */
const CHART_TICK_STYLE = { fontSize: 12, fontFamily: 'inherit' };

const formatModelDate = (value) => {
  if (!value || value.length !== 8) return value || '';
  return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
};

const DAY_NAME_OPTIONS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const ALL_DAYS_VALUE = '__ALL__';
const TIME_PERIOD_DAYS = {
  '1month': 28,
  '3months': 91,
  '6months': 182,
  '1year': 364,
  '2years': 730,
};
const COMPONENTS_PAGE_SIZE = 10;
const SLOT_DEVIATION_THRESHOLD_MINUTES = 15;

/** Parse "HH:MM" or "HH:MM:SS" to minutes from midnight. Returns null if invalid. */
const timeToMinutes = (timeStr) => {
  if (!timeStr || typeof timeStr !== 'string') return null;
  const parts = String(timeStr).trim().split(/[:\s]+/);
  if (parts.length < 2) return null;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
};

const getMedian = (values) => {
  const nums = values.filter((v) => Number.isFinite(v)).sort((a, b) => a - b);
  if (!nums.length) return null;
  const mid = Math.floor(nums.length / 2);
  if (nums.length % 2 === 1) return nums[mid];
  return (nums[mid - 1] + nums[mid]) / 2;
};

/** Slot color by median start/end deviations. */
const getSalePeriodBarColor = ({ startMin, endMin, medianStart, medianEnd }) => {
  const isLateStart =
    Number.isFinite(startMin) &&
    Number.isFinite(medianStart) &&
    (startMin - medianStart > SLOT_DEVIATION_THRESHOLD_MINUTES);
  const isEarlyEnd =
    Number.isFinite(endMin) &&
    Number.isFinite(medianEnd) &&
    (medianEnd - endMin > SLOT_DEVIATION_THRESHOLD_MINUTES);

  if (isLateStart && isEarlyEnd) return '#e53935'; // red
  if (isLateStart) return '#fb8c00'; // orange
  if (isEarlyEnd) return '#f48fb1'; // pink
  return '#66bb6a'; // green
};

const getComponentImpactTag = (componentRow) => {
  const ratio = Number(componentRow?.avg_sale_ratio_1_over_0);
  const inRange = Number(componentRow?.ratio_1_0_in_range) === 1;

  if (!inRange || !Number.isFinite(ratio)) return 'Insignificant';
  if (ratio >= 0.9 && ratio <= 1.1) return 'Neutral';
  if (ratio > 1.1) return 'Positive';
  return 'Negative';
};

const getImpactChipColor = (impact) => {
  if (impact === 'Positive') return 'success';
  if (impact === 'Negative') return 'error';
  if (impact === 'Neutral') return 'default';
  return 'warning';
};

/** Get week key (Sunday date YYYY-MM-DD) for a given date string or Date */
const toNumber = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

const pickForecastValue = (row) => {
  const candidates = [
    row.forecast_qty_component_model,
    row.forecast_qty_component,
    row.forecast_qty_frid,
    row.forecast_qty_old,
    row.forecast_qty,
    row.forecast
  ];
  for (const candidate of candidates) {
    const value = parseFloat(candidate);
    if (Number.isFinite(value)) {
      return value;
    }
  }
  return null;
};

/** Normalize date from collated (DD/MM/YY or DD/MM/YYYY) or return YYYY-MM-DD as-is */
function parseCollatedDate(str) {
  if (!str || typeof str !== 'string') return '';
  const s = str.trim().slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const parts = s.split(/[/-]/);
  if (parts.length === 3) {
    const [a, b, c] = parts;
    if (c.length === 2) {
      const year = 2000 + parseInt(c, 10);
      const day = parseInt(a, 10);
      const month = parseInt(b, 10);
      if (Number.isFinite(day) && Number.isFinite(month)) {
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      }
    }
    if (a.length === 4) {
      return `${a}-${String(parseInt(b, 10)).padStart(2, '0')}-${String(parseInt(c, 10)).padStart(2, '0')}`;
    }
    const day = parseInt(a, 10);
    const month = parseInt(b, 10);
    const year = parseInt(c, 10) + (c.length === 2 ? 2000 : 0);
    if (Number.isFinite(day) && Number.isFinite(month) && Number.isFinite(year)) {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
  }
  return s;
}

const pickMapeValue = (row) => {
  const candidates = [
    row.APE_component_model,
    row.APE_component,
    row.APE_frid,
    row.APE_old,
    row.APE_friw,
    row.MAPE,
    row.mape
  ];
  for (const candidate of candidates) {
    const value = toNumber(candidate);
    if (Number.isFinite(value)) {
      return value;
    }
  }
  return null;
};

const pickMadValue = (row) => {
  const candidates = [
    row.MAD_component_model,
    row.MAD_component,
    row.MAD_frid,
    row.MAD_old,
    row.MAD_friw,
    row.MAD,
    row.mad
  ];
  for (const candidate of candidates) {
    const value = toNumber(candidate);
    if (Number.isFinite(value)) {
      return value;
    }
  }
  return null;
};

const calculateStatusFromMetrics = (mape, mad) => {
  if (mape === null || mad === null || isNaN(mape) || isNaN(mad)) {
    return '';
  }
  
  const accuracyPct = (1 - mape) * 100;
  
  if (accuracyPct > 90) {
    return 'Green';
  } else if (accuracyPct >= 70) {
    if (mad <= 5) {
      return 'Green';
    } else if (mad <= 15) {
      return 'Orange';
    } else {
      return 'Orange';
    }
  } else {
    if (mad <= 5) {
      return 'Green';
    } else if (mad <= 15) {
      return 'Orange';
    } else {
      return 'Red';
    }
  }
};

const FORECAST_BAND_CHART = {
  bandFill: 'rgba(239, 83, 80, 0.18)',
};

/** Recharts ranged Area: dataKey must return [lower, upper] — baseLine callback is not supported per point. */
const forecastBandRangeDataKey = (entry) => {
  const lo = entry?.forecast_lower_plot;
  const hi = entry?.forecast_upper_plot;
  if (lo == null || hi == null || !Number.isFinite(lo) || !Number.isFinite(hi)) {
    return null;
  }
  return [lo, hi];
};

const resolveBandForDate = (dateKey, bandMap, sourceRow = null) => {
  if (sourceRow) {
    const lower = parseFloat(
      sourceRow.forecast_lower
      ?? sourceRow.forecast_qty_lower_component_model
      ?? sourceRow.forecast_qty_lower
    );
    const upper = parseFloat(
      sourceRow.forecast_upper
      ?? sourceRow.forecast_qty_upper_component_model
      ?? sourceRow.forecast_qty_upper
    );
    if (Number.isFinite(lower) && Number.isFinite(upper)) {
      return { lower, upper };
    }
  }
  const fromMap = bandMap?.get(dateKey);
  if (fromMap && Number.isFinite(fromMap.lower) && Number.isFinite(fromMap.upper)) {
    return fromMap;
  }
  return null;
};

const attachBandFields = (entry, bandMap, sourceRow = null) => {
  const band = resolveBandForDate(entry.date, bandMap, sourceRow);
  const hasBand = band && Number.isFinite(band.lower) && Number.isFinite(band.upper);
  const forecastVal = parseFloat(entry.forecast);
  const showBand = hasBand && Number.isFinite(forecastVal) && forecastVal > 0;
  return {
    ...entry,
    forecast_lower_plot: showBand ? band.lower : null,
    forecast_upper_plot: showBand ? band.upper : null,
  };
};

/** True if date falls in model+1 … model+7 for any model date in the list */
const isInAnyForecastWeek = (dateStr, modelDates) => {
  if (!dateStr || !modelDates?.length) return false;
  return modelDates.some((md) => isInLatestForecastWeek(dateStr, md));
};

const resolveChartForecast = (dateKey, forecastByDate, row = null, forecastWeekModelDates = []) => {
  if (!isInAnyForecastWeek(dateKey, forecastWeekModelDates)) {
    return null;
  }
  if (forecastByDate?.has(dateKey)) {
    const v = forecastByDate.get(dateKey);
    return Number.isFinite(v) && v > 0 ? v : 0;
  }
  const fromRow = parseFloat(row?.forecast_component);
  if (Number.isFinite(fromRow) && fromRow > 0) {
    return fromRow;
  }
  return 0;
};

const CustomTooltip = ({ active, payload, label, aggregatedReason = '' }) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const actualPayload = payload.find(p => ['actual', 'actualNormal', 'actualHoliday'].includes(p.dataKey));
  const forecastPayload = payload.find(p => p.dataKey === 'forecast');
  const dataPoint = payload[0]?.payload;
  const description = (dataPoint?.description || '').toString().trim();
  const descCombo1 = (dataPoint?.description_combo1 || '').toString().trim();
  const descCombo2 = (dataPoint?.description_combo2 || '').toString().trim();
  const mgQty = dataPoint?.mg_quantity;
  const potential_reason = (dataPoint?.potential_reason || '').toString().trim();
  const potentialReasonDisplay = formatPotentialReason(potential_reason);
  const potentialReasonText = potentialReasonDisplay.length > 0 ? potentialReasonDisplay.join('; ') : 'None';
  const aggregatedDisplay = formatAggregatedPotentialReason(aggregatedReason);
  const aggregatedText = aggregatedDisplay.length > 0 ? aggregatedDisplay.join('; ') : null;
  const dualMenus = [descCombo1, descCombo2].filter(Boolean);

  return (
    <div style={{
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      border: '1px solid #ccc',
      borderRadius: '4px',
      padding: '8px',
      fontSize: '12px'
    }}>
      <div style={{ fontWeight: 600, marginBottom: '4px' }}>{formatDateWithWeekday(label)}</div>
      {actualPayload && (
        <div style={{ color: '#1976d2', marginBottom: '2px' }}>
          Actual: {actualPayload.value}
        </div>
      )}
      {forecastPayload && forecastPayload.value !== null && forecastPayload.value !== undefined && (
        <div style={{ color: '#ef5350', marginBottom: '2px' }}>
          Forecast: {forecastPayload.value}
        </div>
      )}
      {dataPoint?.forecast_lower_plot != null && dataPoint?.forecast_upper_plot != null && (
        <div style={{ color: '#ef5350', marginBottom: '2px' }}>
          Range: {dataPoint.forecast_lower_plot}–{dataPoint.forecast_upper_plot}
        </div>
      )}
      {mgQty != null && Number.isFinite(mgQty) && (
        <div style={{ color: '#8e24aa', marginBottom: '2px' }}>
          MG: {mgQty}
        </div>
      )}
      {payload.some(p => p.dataKey === 'avg_units_sold') && (payload.find(p => p.dataKey === 'avg_units_sold')?.value ?? null) != null && (
        <div style={{ color: '#43a047', marginBottom: '4px' }}>
          Avg. Units Sold: {(payload.find(p => p.dataKey === 'avg_units_sold').value).toFixed(1)}
        </div>
      )}
      <div style={{ marginTop: '4px', maxWidth: 280 }}>
        <span style={{ fontWeight: 600, color: '#666' }}>Potential Reason: </span>
        <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {potentialReasonText}
        </span>
      </div>
      {aggregatedText && (
        <div style={{ marginTop: '4px', maxWidth: 280 }}>
          <span style={{ fontWeight: 600, color: '#666' }}>Model-level: </span>
          <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {aggregatedText}
          </span>
        </div>
      )}
      {dualMenus.length > 0 ? (
        <div style={{ marginTop: '4px', maxWidth: 280 }}>
          <span style={{ fontWeight: 600, color: '#666' }}>Menu: </span>
          {dualMenus.map((d, i) => (
            <div key={i} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {i === 0 ? 'Combo 1' : 'Combo 2'}: {toProperCase(d)}
            </div>
          ))}
        </div>
      ) : description ? (
        <div style={{ marginTop: '4px', maxWidth: 280 }}>
          <span style={{ fontWeight: 600, color: '#666' }}>Description: </span>
          <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {toProperCase(description)}
          </span>
        </div>
      ) : null}
    </div>
  );
};

function ItemAccuracyTrends() {
  const { selectedRunDate, foldersByRunDate } = useFolder();
  const [collatedData, setCollatedData] = useState([]);
  const [collatedAggregatedData, setCollatedAggregatedData] = useState([]);
  const [loadingCollated, setLoadingCollated] = useState(false);
  const [collatedError, setCollatedError] = useState(null);
  const [dailySalesData, setDailySalesData] = useState([]);
  const [loadingSales, setLoadingSales] = useState(false);
  const [salesError, setSalesError] = useState(null);
  const [selectedFoodcourt, setSelectedFoodcourt] = useState('');
  const [selectedRestaurant, setSelectedRestaurant] = useState('');
  const [selectedItem, setSelectedItem] = useState('');
  const [fcMapping, setFcMapping] = useState({});
  const [restaurantMapping, setRestaurantMapping] = useState({});
  const [timePeriod, setTimePeriod] = useState('6months');
  const [selectedDays, setSelectedDays] = useState(DAY_NAME_OPTIONS);
  const [dateRange, setDateRange] = useState([null, null]);
  const [dominantSession, setDominantSession] = useState(null);
  const [friComponentStats, setFriComponentStats] = useState([]);
  const [loadingFriComponentStats, setLoadingFriComponentStats] = useState(false);
  const [friComponentStatsError, setFriComponentStatsError] = useState(null);
  const [selectedComponentDetail, setSelectedComponentDetail] = useState(null);
  const [selectedComponentCategory, setSelectedComponentCategory] = useState('All');
  const [componentPage, setComponentPage] = useState(0);
  const [excludeHolidays, setExcludeHolidays] = useState(false);
  const [allFolderBandData, setAllFolderBandData] = useState([]);

  const latestFolder = useMemo(() => {
    if (!selectedRunDate) return null;
    const folders = foldersByRunDate[selectedRunDate] || [];
    if (folders.length === 0) return null;
    return folders[folders.length - 1];
  }, [selectedRunDate, foldersByRunDate]);

  const latestModelDate = latestFolder?.modelDate || '';

  const forecastBandByDate = useMemo(() => {
    const map = new Map();
    (allFolderBandData || []).forEach(({ date, lower, upper }) => {
      if (date && Number.isFinite(lower) && Number.isFinite(upper)) {
        map.set(date, { lower, upper });
      }
    });
    (dailySalesData || []).forEach((row) => {
      const dateKey = (row.date || '').toString().slice(0, 10);
      const lower = parseFloat(row.forecast_lower);
      const upper = parseFloat(row.forecast_upper);
      if (dateKey && Number.isFinite(lower) && Number.isFinite(upper)) {
        map.set(dateKey, { lower, upper });
      }
    });
    return map;
  }, [allFolderBandData, dailySalesData]);

  const filteredCollatedData = useMemo(() => {
    let rows = filterAllowedItems(filterOptumSkus(collatedData || []));
    if (excludeHolidays && rows.length > 0) {
      rows = rows.filter(row => !isModelDateHolidayWeekItem(row.model_date || row.modelDate));
    }
    if (latestModelDate && rows.length > 0) {
      rows = rows.map(row => stripLatestWeekActuals(row, latestModelDate, selectedRunDate));
    }
    return rows;
  }, [collatedData, excludeHolidays, latestModelDate, selectedRunDate]);

  const filteredCollatedAggregatedData = useMemo(() => {
    let rows = filterAllowedItems(filterOptumSkus(collatedAggregatedData || []));
    if (excludeHolidays && rows.length > 0) {
      rows = rows.filter(row => !isModelDateHolidayWeekItem(row.model_date || row.modelDate));
    }
    if (latestModelDate && rows.length > 0) {
      rows = rows.map(row => stripLatestWeekAggregatedActuals(row, latestModelDate, selectedRunDate));
    }
    return rows;
  }, [collatedAggregatedData, excludeHolidays, latestModelDate, selectedRunDate]);

  useEffect(() => {
    const loadMappings = async () => {
      try {
        const [fcrResponse, fcMappingRes, restaurantsRes] = await Promise.all([
          axios.get('/api/fcr-mapping').catch(() => ({ data: [] })),
          axios.get('/api/fc-mapping').catch(() => ({ data: [] })),
          axios.get('/api/restaurants').catch(() => ({ data: [] }))
        ]);
        
        const fcMap = {};
        const restMap = {};
        
        if (fcMappingRes.data && fcMappingRes.data.length > 0) {
          fcMappingRes.data.forEach(row => {
            const fcId = (row.foodcourtid || '').toString().trim();
            const fcName = (row.foodcourtname || '').toString().trim();
            if (fcId && fcName) {
              fcMap[fcId] = fcName;
            }
          });
        }
        
        if (fcrResponse.data && fcrResponse.data.length > 0) {
          fcrResponse.data.forEach(row => {
            const fcId = (row.foodcourtid || row.foodcourt_id || '').toString().trim();
            const fcName = (row.foodcourtname || row.foodcourt_name || '').toString().trim();
            const restId = (row.restaurant || row.restaurant_id || '').toString().trim();
            const restName = (row.restaurantname || row.restaurant_name || '').toString().trim();
            
            if (fcId && fcName && !fcMap[fcId]) {
              fcMap[fcId] = fcName;
            }
            if (restId && restName) {
              restMap[restId] = restName;
            }
          });
        }
        
        if (restaurantsRes.data && restaurantsRes.data.length > 0) {
          restaurantsRes.data.forEach(rest => {
            const restId = (rest.id || '').toString().trim();
            const restName = (rest.name || '').toString().trim();
            if (restId && restName && !restMap[restId]) {
              restMap[restId] = restName;
            }
          });
        }
        
        setFcMapping(fcMap);
        setRestaurantMapping(restMap);
      } catch (err) {
        console.error('Error loading mappings:', err);
      }
    };
    
    loadMappings();
  }, []);

  useEffect(() => {
    const fetchCollatedData = async () => {
      if (!selectedRunDate) {
        setCollatedData([]);
        setCollatedAggregatedData([]);
        return;
      }
      
      setLoadingCollated(true);
      setCollatedError(null);
      try {
        const [comparisonRes, aggregatedRes] = await Promise.all([
          axios.get(`/api/collated-data/${selectedRunDate}`, { params: { file: 'collated_comparison' } }),
          axios.get(`/api/collated-data/${selectedRunDate}`, { params: { file: 'collated_comparison_aggregated' } }).catch(() => ({ data: [] }))
        ]);
        setCollatedData(comparisonRes.data || []);
        setCollatedAggregatedData(aggregatedRes?.data || []);
      } catch (err) {
        console.error('Error fetching collated data:', err);
        setCollatedError('Failed to load collated comparison data.');
      } finally {
        setLoadingCollated(false);
      }
    };
    
    fetchCollatedData();
  }, [selectedRunDate]);

  /** FRIs with at least 1 day scored in at least one of the last 4 model dates (include all items with any data in dropdowns) */
  const validFriKeys = useMemo(() => {
    const rows = filteredCollatedAggregatedData || [];
    if (rows.length === 0) return new Set();
    const dates = [...new Set(rows.map((r) => (r.model_date || r.modelDate || '').toString().trim()).filter(Boolean))].sort();
    const last4 = new Set(dates.slice(-4));
    const keys = new Set();
    rows.forEach((row) => {
      const md = (row.model_date || row.modelDate || '').toString().trim();
      if (!md || !last4.has(md)) return;
      const numDays = parseFloat(row.num_days || 0) || 0;
      if (numDays < 1) return;
      const fc = (row.foodcourtid || '').toString().trim();
      const rest = (row.restaurant || '').toString().trim();
      const item = (row.itemname || '').toString().trim();
      if (fc && rest && item) keys.add(`${fc}||${rest}||${item}`);
    });
    return keys;
  }, [filteredCollatedAggregatedData]);

  const foodcourtOptions = useMemo(() => {
    const map = new Map();
    const useFilter = validFriKeys.size > 0;
    filteredCollatedData.forEach(row => {
      const fc = (row.foodcourtid || '').toString().trim();
      const rest = (row.restaurant || '').toString().trim();
      const item = (row.itemname || '').toString().trim();
      if (!fc) return;
      if (useFilter && !validFriKeys.has(`${fc}||${rest}||${item}`)) return;
      map.set(fc, fcMapping[fc] || fc);
    });
    return Array.from(map.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([id, name]) => ({ id, name }));
  }, [filteredCollatedData, fcMapping, validFriKeys]);

  const restaurantOptions = useMemo(() => {
    const map = new Map();
    const useFilter = validFriKeys.size > 0;
    filteredCollatedData.forEach(row => {
      if (selectedFoodcourt && row.foodcourtid !== selectedFoodcourt) return;
      const fc = (row.foodcourtid || '').toString().trim();
      const rest = (row.restaurant || '').toString().trim();
      const item = (row.itemname || '').toString().trim();
      if (useFilter && !validFriKeys.has(`${fc}||${rest}||${item}`)) return;
      if (!rest) return;
      map.set(rest, restaurantMapping[rest] || rest);
    });
    return Array.from(map.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([id, name]) => ({ id, name }));
  }, [filteredCollatedData, selectedFoodcourt, restaurantMapping, validFriKeys]);

  const itemOptions = useMemo(() => {
    const map = new Map();
    const useFilter = validFriKeys.size > 0;
    let hasCombo1 = false;
    let hasCombo2 = false;
    filteredCollatedData.forEach(row => {
      if (selectedFoodcourt && row.foodcourtid !== selectedFoodcourt) return;
      if (selectedRestaurant && row.restaurant !== selectedRestaurant) return;
      const fc = (row.foodcourtid || '').toString().trim();
      const rest = (row.restaurant || '').toString().trim();
      const item = (row.itemname || '').toString().trim();
      if (useFilter && !validFriKeys.has(`${fc}||${rest}||${item}`)) return;
      if (!item) return;
      if (isComboAggregateItem(item)) return; // synthetic only
      const key = normalizeItemKey(item);
      if (key === 'non veg combo 1') hasCombo1 = true;
      if (key === 'non veg combo 2') hasCombo2 = true;
      map.set(item, displayItemLabel(item));
    });
    if (hasCombo1 && hasCombo2) {
      map.set(COMBO_AGGREGATE_ITEMNAME, displayItemLabel(COMBO_AGGREGATE_ITEMNAME));
    }
    return Array.from(map.entries())
      .sort((a, b) => compareItemDisplayOrder(a[0], b[0]))
      .map(([id, name]) => ({ id, name }));
  }, [filteredCollatedData, selectedFoodcourt, selectedRestaurant, validFriKeys]);

  const isAggregateItem = isComboAggregateItem(selectedItem);

  /** Day-level rows for the selected item (synthetic sum for Non Veg Lunch). */
  const selectedItemDayRows = useMemo(() => {
    if (!selectedFoodcourt || !selectedRestaurant || !selectedItem) return [];
    if (isComboAggregateItem(selectedItem)) {
      return buildComboAggregateStrictRows(
        filteredCollatedData,
        selectedFoodcourt,
        selectedRestaurant
      );
    }
    return (filteredCollatedData || []).filter(
      (row) =>
        String(row.foodcourtid || '').trim() === String(selectedFoodcourt).trim() &&
        String(row.restaurant || '').trim() === String(selectedRestaurant).trim() &&
        String(row.itemname || '').trim() === String(selectedItem).trim()
    );
  }, [filteredCollatedData, selectedFoodcourt, selectedRestaurant, selectedItem]);

  useEffect(() => {
    if (selectedFoodcourt && !foodcourtOptions.find(fc => fc.id === selectedFoodcourt)) {
      setSelectedFoodcourt('');
    }
  }, [foodcourtOptions, selectedFoodcourt]);

  useEffect(() => {
    if (selectedRestaurant && !restaurantOptions.find(rest => rest.id === selectedRestaurant)) {
      setSelectedRestaurant('');
    }
  }, [restaurantOptions, selectedRestaurant]);

  useEffect(() => {
    if (selectedItem && !itemOptions.find(item => item.id === selectedItem)) {
      setSelectedItem('');
    }
  }, [itemOptions, selectedItem]);

  useEffect(() => {
    if (!selectedFoodcourt && foodcourtOptions.length > 0) {
      setSelectedFoodcourt(foodcourtOptions[0].id);
    }
  }, [foodcourtOptions, selectedFoodcourt]);

  useEffect(() => {
    if (selectedFoodcourt && !selectedRestaurant && restaurantOptions.length > 0) {
      setSelectedRestaurant(restaurantOptions[0].id);
    }
  }, [selectedFoodcourt, selectedRestaurant, restaurantOptions]);

  useEffect(() => {
    if (selectedRestaurant && !selectedItem && itemOptions.length > 0) {
      setSelectedItem(itemOptions[0].id);
    }
  }, [selectedRestaurant, selectedItem, itemOptions]);

  useEffect(() => {
    const loadDailySales = async () => {
      if (!selectedFoodcourt || !selectedRestaurant || !selectedItem) {
        setDailySalesData([]);
        return;
      }
      
      if (!latestFolder?.name) {
        setSalesError('No latest model folder found for the selected run date.');
        return;
      }
      
      setLoadingSales(true);
      setSalesError(null);
      try {
        if (isComboAggregateItem(selectedItem)) {
          const [r1, r2] = await Promise.all([
            axios.get('/api/daily-sales', {
              params: {
                foodcourtid: selectedFoodcourt,
                restaurant: selectedRestaurant,
                itemname: 'Non Veg Combo 1',
                folder: latestFolder.name,
              },
            }),
            axios.get('/api/daily-sales', {
              params: {
                foodcourtid: selectedFoodcourt,
                restaurant: selectedRestaurant,
                itemname: 'Non Veg Combo 2',
                folder: latestFolder.name,
              },
            }),
          ]);
          setDailySalesData(aggregateDailySalesByDate(r1.data || [], r2.data || []));
        } else {
          const response = await axios.get('/api/daily-sales', {
            params: {
              foodcourtid: selectedFoodcourt,
              restaurant: selectedRestaurant,
              itemname: selectedItem,
              folder: latestFolder.name
            }
          });
          setDailySalesData(response.data || []);
        }
      } catch (err) {
        console.error('Error fetching daily sales data:', err);
        setSalesError('Failed to load daily sales data.');
      } finally {
        setLoadingSales(false);
      }
    };
    
    loadDailySales();
  }, [selectedFoodcourt, selectedRestaurant, selectedItem, latestFolder]);

  useEffect(() => {
    const loadForecastBands = async () => {
      if (!selectedFoodcourt || !selectedRestaurant || !selectedItem || !selectedRunDate) {
        setAllFolderBandData([]);
        return;
      }
      try {
        if (isComboAggregateItem(selectedItem)) {
          const [b1, b2] = await Promise.all([
            axios.get('/api/forecast-bands', {
              params: {
                foodcourtid: selectedFoodcourt,
                restaurant: selectedRestaurant,
                itemname: 'Non Veg Combo 1',
                runDate: selectedRunDate,
              },
            }),
            axios.get('/api/forecast-bands', {
              params: {
                foodcourtid: selectedFoodcourt,
                restaurant: selectedRestaurant,
                itemname: 'Non Veg Combo 2',
                runDate: selectedRunDate,
              },
            }),
          ]);
          const map = new Map();
          const add = (rows) => {
            (rows || []).forEach((r) => {
              const d = String(r.date || '').slice(0, 10);
              if (!d) return;
              const cur = map.get(d) || { date: d, lower: null, upper: null };
              cur.lower = sumFinite(cur.lower, r.lower);
              cur.upper = sumFinite(cur.upper, r.upper);
              map.set(d, cur);
            });
          };
          add(b1.data);
          add(b2.data);
          setAllFolderBandData(Array.from(map.values()));
        } else {
          const response = await axios.get('/api/forecast-bands', {
            params: {
              foodcourtid: selectedFoodcourt,
              restaurant: selectedRestaurant,
              itemname: selectedItem,
              runDate: selectedRunDate,
            },
          });
          setAllFolderBandData(response.data || []);
        }
      } catch (err) {
        console.error('Error loading forecast bands:', err);
        setAllFolderBandData([]);
      }
    };
    loadForecastBands();
  }, [selectedFoodcourt, selectedRestaurant, selectedItem, selectedRunDate]);

  useEffect(() => {
    const loadDominantSession = async () => {
      if (!selectedFoodcourt || !selectedRestaurant || !selectedItem || !latestFolder?.name) {
        setDominantSession(null);
        return;
      }
      try {
        if (isComboAggregateItem(selectedItem)) {
          const [r1, r2] = await Promise.all([
            axios.get('/api/dominant-session', {
              params: {
                foodcourtid: selectedFoodcourt,
                restaurant: selectedRestaurant,
                itemname: 'Non Veg Combo 1',
                folder: latestFolder.name,
              },
            }).catch(() => ({ data: {} })),
            axios.get('/api/dominant-session', {
              params: {
                foodcourtid: selectedFoodcourt,
                restaurant: selectedRestaurant,
                itemname: 'Non Veg Combo 2',
                folder: latestFolder.name,
              },
            }).catch(() => ({ data: {} })),
          ]);
          const s1 = (r1.data?.dominant_session ?? '').toString().trim();
          const s2 = (r2.data?.dominant_session ?? '').toString().trim();
          setDominantSession(s1 || s2 || null);
          return;
        }
        const response = await axios.get('/api/dominant-session', {
          params: {
            foodcourtid: selectedFoodcourt,
            restaurant: selectedRestaurant,
            itemname: selectedItem,
            folder: latestFolder.name
          }
        });
        setDominantSession(response.data?.dominant_session ?? null);
      } catch {
        setDominantSession(null);
      }
    };
    loadDominantSession();
  }, [selectedFoodcourt, selectedRestaurant, selectedItem, latestFolder]);

  const [mgData, setMgData] = useState([]);
  useEffect(() => {
    const loadMg = async () => {
      if (!selectedFoodcourt || !selectedRestaurant || !selectedItem) {
        setMgData([]);
        return;
      }
      const mgType = mgTypeForItem(selectedItem);
      if (!mgType) {
        setMgData([]);
        return;
      }
      try {
        const response = await axios.get('/api/mg-data', {
          params: { foodcourtid: selectedFoodcourt, restaurant: selectedRestaurant },
        });
        setMgData(
          (response.data || []).filter(
            (r) => String(r.item_type || '').toLowerCase() === mgType
          )
        );
      } catch {
        setMgData([]);
      }
    };
    loadMg();
  }, [selectedFoodcourt, selectedRestaurant, selectedItem]);

  useEffect(() => {
    const loadFriComponentStats = async () => {
      if (!selectedFoodcourt || !selectedRestaurant || !selectedItem || !latestFolder?.name || isComboAggregateItem(selectedItem)) {
        setFriComponentStats([]);
        setLoadingFriComponentStats(false);
        return;
      }
      setLoadingFriComponentStats(true);
      setFriComponentStatsError(null);
      try {
        const response = await axios.get('/api/fri-components-stats', {
          params: {
            foodcourtid: selectedFoodcourt,
            restaurant: selectedRestaurant,
            itemname: selectedItem,
            folder: latestFolder.name
          }
        });
        setFriComponentStats(response.data || []);
      } catch (err) {
        setFriComponentStats([]);
        setFriComponentStatsError(err.message || 'Error loading component stats');
      } finally {
        setLoadingFriComponentStats(false);
      }
    };
    loadFriComponentStats();
  }, [selectedFoodcourt, selectedRestaurant, selectedItem, latestFolder]);

  const handleDayFilterChange = (event) => {
    const value = event.target.value;
    const rawValues = typeof value === 'string' ? value.split(',') : value;
    if (rawValues.includes(ALL_DAYS_VALUE)) {
      if (selectedDays.length === DAY_NAME_OPTIONS.length) {
        setSelectedDays([]);
        return;
      }
      setSelectedDays(DAY_NAME_OPTIONS);
      return;
    }
    const effectiveValues = rawValues.filter((item) => item !== ALL_DAYS_VALUE);
    if (effectiveValues.length === DAY_NAME_OPTIONS.length) {
      setSelectedDays(DAY_NAME_OPTIONS);
      return;
    }
    setSelectedDays(effectiveValues);
  };

  /** potential_reason from collated_comparison_aggregated for selected FRI and latest model_date (reasons_pre flags) */
  const aggregatedReasonForChart = useMemo(() => {
    if (!selectedFoodcourt || !selectedRestaurant || !selectedItem || !latestModelDate || !filteredCollatedAggregatedData?.length) return '';
    const fc = String(selectedFoodcourt || '').trim();
    const rest = String(selectedRestaurant || '').trim();
    const item = String(selectedItem || '').trim();
    const targetModel = String(latestModelDate || '').trim();
    const row = filteredCollatedAggregatedData.find(r =>
      String(r.foodcourtid || '').trim() === fc &&
      String(r.restaurant || '').trim() === rest &&
      String(r.itemname || '').trim() === item &&
      String(r.model_date || r.modelDate || '').trim() === targetModel
    );
    return (row?.potential_reason ?? row?.potentialReason ?? '').toString().trim();
  }, [filteredCollatedAggregatedData, selectedFoodcourt, selectedRestaurant, selectedItem, latestModelDate]);

  /** forecast and potential_reason by date from same row (so tooltip shows reason from the row that has the forecast) */
  const { forecastByDate, potentialReasonByDate } = useMemo(() => {
    const forecastMap = new Map();
    const reasonMap = new Map();
    if (!selectedFoodcourt || !selectedRestaurant || !selectedItem) {
      return { forecastByDate: forecastMap, potentialReasonByDate: reasonMap };
    }
    const fc = String(selectedFoodcourt || '').trim();
    const rest = String(selectedRestaurant || '').trim();
    const item = String(selectedItem || '').trim();
    if (isComboAggregateItem(item)) {
      const aggregated = buildComboAggregateStrictRows(filteredCollatedData, fc, rest);
      aggregated.forEach((row) => {
        const dateKey = parseCollatedDate((row.date || row.Date || '').toString().trim());
        if (!dateKey) return;
        const value = pickForecastValue(row);
        if (Number.isFinite(value) && value > 0) {
          forecastMap.set(dateKey, value);
        }
        reasonMap.set(dateKey, '');
      });
      return { forecastByDate: forecastMap, potentialReasonByDate: reasonMap };
    }
    filteredCollatedData.forEach(row => {
      if (String(row.foodcourtid || '').trim() !== fc) return;
      if (String(row.restaurant || '').trim() !== rest) return;
      if (String(row.itemname || '').trim() !== item) return;
      const dateKey = parseCollatedDate((row.date || row.Date || '').toString().trim());
      if (!dateKey) return;
      const value = pickForecastValue(row);
      if (Number.isFinite(value) && value > 0) {
        forecastMap.set(dateKey, value);
      }
      const pr = (row.potential_reason ?? row.potentialReason ?? '').toString().trim();
      reasonMap.set(dateKey, pr);
    });
    return { forecastByDate: forecastMap, potentialReasonByDate: reasonMap };
  }, [filteredCollatedData, selectedFoodcourt, selectedRestaurant, selectedItem]);

  /** All model dates for selected FRI — each defines a forecast week (model+1 … model+7) */
  const forecastWeekModelDates = useMemo(() => {
    if (!selectedFoodcourt || !selectedRestaurant || !selectedItem) return [];
    const fc = String(selectedFoodcourt || '').trim();
    const rest = String(selectedRestaurant || '').trim();
    const item = String(selectedItem || '').trim();
    const modelDates = new Set();
    const matchItem = (rowItem) => {
      if (isComboAggregateItem(item)) return isComboSourceItem(rowItem);
      return String(rowItem || '').trim() === item;
    };
    filteredCollatedData.forEach((row) => {
      if (String(row.foodcourtid || '').trim() !== fc) return;
      if (String(row.restaurant || '').trim() !== rest) return;
      if (!matchItem(row.itemname)) return;
      const md = String(row.model_date || row.modelDate || '').trim();
      if (md) modelDates.add(md);
    });
    return Array.from(modelDates).sort();
  }, [filteredCollatedData, selectedFoodcourt, selectedRestaurant, selectedItem]);

  const modelDateObj = useMemo(() => {
    if (!latestModelDate || latestModelDate.length !== 8) return null;
    const year = latestModelDate.slice(0, 4);
    const month = latestModelDate.slice(4, 6);
    const day = latestModelDate.slice(6, 8);
    const date = new Date(`${year}-${month}-${day}`);
    return Number.isNaN(date.getTime()) ? null : date;
  }, [latestModelDate]);

  const dataExtent = useMemo(() => {
    const allDates = [];
    dailySalesData.forEach(row => {
      const dateKey = (row.date || '').toString().slice(0, 10);
      const d = /^\d{4}-\d{2}-\d{2}$/.test(dateKey) ? new Date(`${dateKey}T00:00:00`) : new Date(row.date);
      if (!Number.isNaN(d.getTime())) {
        allDates.push(d);
      }
    });
    forecastByDate.forEach((_val, key) => {
      const d = new Date(`${key}T00:00:00`);
      if (!Number.isNaN(d.getTime())) {
        allDates.push(d);
      }
    });
    const latestWindow = latestModelDate ? getForecastWindow(latestModelDate) : null;
    if (latestWindow?.end) {
      const d = new Date(`${latestWindow.end}T00:00:00`);
      if (!Number.isNaN(d.getTime())) {
        allDates.push(d);
      }
    }
    if (allDates.length === 0 && selectedFoodcourt && selectedRestaurant && selectedItem) {
      filteredCollatedData.forEach(row => {
        if (String(row.foodcourtid || '').trim() !== selectedFoodcourt || String(row.restaurant || '').trim() !== selectedRestaurant || String(row.itemname || '').trim() !== selectedItem) return;
        const dateKey = parseCollatedDate((row.date || row.Date || '').toString().trim());
        if (!dateKey) return;
        const d = new Date(dateKey);
        if (!Number.isNaN(d.getTime())) allDates.push(d);
      });
    }
    if (allDates.length === 0) {
      return { minDate: null, maxDate: null };
    }
    const minDate = new Date(Math.min(...allDates.map(d => d.getTime())));
    minDate.setHours(0, 0, 0, 0);
    const maxDate = new Date(Math.max(...allDates.map(d => d.getTime())));
    maxDate.setHours(0, 0, 0, 0);
    return { minDate, maxDate };
  }, [dailySalesData, forecastByDate, filteredCollatedData, selectedFoodcourt, selectedRestaurant, selectedItem, latestModelDate]);

  useEffect(() => {
    if (timePeriod === 'custom') return;
    if (!dataExtent.minDate || !dataExtent.maxDate) {
      setDateRange([null, null]);
      return;
    }
    const days = TIME_PERIOD_DAYS[timePeriod] || TIME_PERIOD_DAYS['6months'];
    const end = new Date(dataExtent.maxDate);
    const start = new Date(dataExtent.maxDate);
    start.setDate(start.getDate() - days);
    if (start < dataExtent.minDate) {
      start.setTime(dataExtent.minDate.getTime());
    }
    setDateRange([start, end]);
  }, [timePeriod, dataExtent.minDate, dataExtent.maxDate]);

  const handleDateRangeChange = (nextRange) => {
    setDateRange(nextRange);
    setTimePeriod('custom');
  };

  const chartData = useMemo(() => {
    const [startDate, endDate] = dateRange || [];
    const activeDaySet = new Set(selectedDays.length ? selectedDays : DAY_NAME_OPTIONS);
    const buildChartRow = (dateKey, row, actualQty = null) => {
      const hideActual = isInLatestForecastWeek(dateKey, latestModelDate)
        && isActualPending(dateKey, selectedRunDate);
          return attachBandFields({
            date: dateKey,
            actual: hideActual
              ? null
              : (actualQty != null
                ? actualQty
                : (Number.isFinite(parseFloat(row?.quantity)) ? parseFloat(row.quantity) : 0)),
            forecast: resolveChartForecast(dateKey, forecastByDate, row, forecastWeekModelDates),
            description: (row?.description || '').toString().trim(),
            description_combo1: (row?.description_combo1 || '').toString().trim(),
            description_combo2: (row?.description_combo2 || '').toString().trim(),
            potential_reason: potentialReasonByDate.get(dateKey) ?? ''
          }, forecastBandByDate, row);
    };
    const passesDayFilter = (dateKey) => {
      const date = new Date(`${dateKey}T00:00:00`);
      if (Number.isNaN(date.getTime())) return false;
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
      if (!activeDaySet.has(dayName)) return false;
      if (startDate && endDate && (date < startDate || date > endDate)) return false;
      return true;
    };
    const mergeLatestForecastWindowDates = (rows) => {
      if (!latestModelDate || !selectedFoodcourt || !selectedRestaurant || !selectedItem) {
        return rows;
      }
      const window = getForecastWindow(latestModelDate);
      if (!window) return rows;
      const existing = new Set(rows.map((row) => row.date));
      const extras = [];
      const sourceRows = isComboAggregateItem(selectedItem)
        ? selectedItemDayRows
        : filteredCollatedData.filter((row) => {
            if (String(row.foodcourtid || '').trim() !== selectedFoodcourt) return false;
            if (String(row.restaurant || '').trim() !== selectedRestaurant) return false;
            return String(row.itemname || '').trim() === selectedItem;
          });
      sourceRows.forEach((row) => {
        const dateKey = parseCollatedDate((row.date || row.Date || '').toString().trim());
        if (!dateKey || existing.has(dateKey)) return;
        if (dateKey < window.start || dateKey > window.end) return;
        if (!passesDayFilter(dateKey)) return;
        const actual = Number.isFinite(parseFloat(row.actual_qty)) ? parseFloat(row.actual_qty) : 0;
        extras.push(buildChartRow(dateKey, {
          forecast_component: pickForecastValue(row),
          description: row.description,
          description_combo1: row.description_combo1,
          description_combo2: row.description_combo2,
        }, actual));
      });
      if (extras.length === 0) return rows;
      return [...rows, ...extras].sort((a, b) => a.date.localeCompare(b.date));
    };

    if (dailySalesData && dailySalesData.length > 0) {
      const rows = dailySalesData
        .filter(row => {
          const dateKey = (row.date || '').toString().slice(0, 10);
          return passesDayFilter(dateKey);
        })
        .map(row => {
          const dateKey = (row.date || '').toString().slice(0, 10);
          return buildChartRow(dateKey, row);
        })
        .filter(row => row.date);
      return mergeLatestForecastWindowDates(rows);
    }
    if (!selectedFoodcourt || !selectedRestaurant || !selectedItem || !selectedItemDayRows?.length) return [];
    const fromCollated = [];
    selectedItemDayRows.forEach(row => {
      const dateKey = parseCollatedDate((row.date || row.Date || '').toString().trim());
      if (!dateKey) return;
      const date = new Date(dateKey);
      if (Number.isNaN(date.getTime())) return;
      if (startDate && endDate) {
        const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
        if (!activeDaySet.has(dayName)) return;
        if (date < startDate || date > endDate) return;
      }
      const actual = Number.isFinite(parseFloat(row.actual_qty)) ? parseFloat(row.actual_qty) : 0;
      const forecast = pickForecastValue(row);
      fromCollated.push(attachBandFields({
        date: dateKey,
        actual,
        forecast: resolveChartForecast(dateKey, forecastByDate, { forecast_component: forecast }, forecastWeekModelDates),
        description: (row.description || '').toString().trim(),
        description_combo1: (row.description_combo1 || '').toString().trim(),
        description_combo2: (row.description_combo2 || '').toString().trim(),
        potential_reason: (row.potential_reason ?? row.potentialReason ?? '').toString().trim()
      }, forecastBandByDate));
    });
    return fromCollated.sort((a, b) => a.date.localeCompare(b.date));
  }, [dailySalesData, forecastByDate, potentialReasonByDate, dateRange, selectedDays, filteredCollatedData, selectedItemDayRows, selectedFoodcourt, selectedRestaurant, selectedItem, latestModelDate, selectedRunDate, forecastBandByDate, forecastWeekModelDates]);

  /** Sale period data: first_order_time to last_order_time per day, colored by revenue_coverage */
  const salePeriodTrendData = useMemo(() => {
    if (!selectedFoodcourt || !selectedRestaurant || !selectedItem) return [];
    const [startDate, endDate] = dateRange || [];
    const activeDaySet = new Set(selectedDays.length ? selectedDays : DAY_NAME_OPTIONS);
    const seen = new Set();
    const result = [];
    const sourceRows = isComboAggregateItem(selectedItem)
      ? selectedItemDayRows
      : (filteredCollatedData || []).filter(
          (row) =>
            row.foodcourtid === selectedFoodcourt &&
            row.restaurant === selectedRestaurant &&
            row.itemname === selectedItem
        );
    sourceRows.forEach(row => {
      const dateStr = parseCollatedDate((row.date || row.Date || '').toString().trim()) || (row.date || '').toString().slice(0, 10);
      if (!dateStr || seen.has(dateStr)) return;
      const d = new Date(dateStr);
      if (Number.isNaN(d.getTime())) return;
      const dayName = d.toLocaleDateString('en-US', { weekday: 'long' });
      if (!activeDaySet.has(dayName)) return;
      if (startDate && endDate && (d < startDate || d > endDate)) return;
      const startMin = timeToMinutes(row.first_order_time);
      const endMin = timeToMinutes(row.last_order_time);
      if (startMin == null || endMin == null || endMin <= startMin) return;
      seen.add(dateStr);
      const revenueCoverage = parseFloat(row.revenue_coverage);
      result.push({
        date: dateStr,
        offset: startMin,
        span: endMin - startMin,
        endMin,
        first_order_time: row.first_order_time || '',
        last_order_time: row.last_order_time || '',
        revenue_coverage: Number.isFinite(revenueCoverage) ? revenueCoverage : null,
        color: '#66bb6a'
      });
    });
    const sorted = result.sort((a, b) => a.date.localeCompare(b.date));
    const medianStart = getMedian(sorted.map((r) => r.offset));
    const medianEnd = getMedian(sorted.map((r) => r.endMin));

    return sorted.map((r) => ({
      ...r,
      color: getSalePeriodBarColor({
        startMin: r.offset,
        endMin: r.endMin,
        medianStart,
        medianEnd
      })
    }));
  }, [filteredCollatedData, selectedItemDayRows, selectedFoodcourt, selectedRestaurant, selectedItem, dateRange, selectedDays]);

  /** Y-axis domain (min, max minutes) for Sale Period chart, derived from data */
  const salePeriodYDomain = useMemo(() => {
    if (!salePeriodTrendData.length) return [0, 1440];
    let minMin = Infinity;
    let maxMin = -Infinity;
    salePeriodTrendData.forEach(({ offset, span }) => {
      minMin = Math.min(minMin, offset);
      maxMin = Math.max(maxMin, offset + span);
    });
    if (!Number.isFinite(minMin) || !Number.isFinite(maxMin)) return [0, 1440];
    const pad = 15;
    const yMin = Math.floor(minMin / pad) * pad;
    const yMax = Math.ceil(maxMin / pad) * pad;
    return [Math.max(0, yMin), Math.min(1440, Math.max(yMax, yMin + 60))];
  }, [salePeriodTrendData]);

  /** Unified date list so Daily Level Accuracy and Session Slot-Wise Distribution share the same x-axis dates */
  const alignmentDates = useMemo(() => {
    const set = new Set();
    chartData.forEach((r) => r.date && set.add(r.date));
    salePeriodTrendData.forEach((r) => r.date && set.add(r.date));
    return Array.from(set).sort();
  }, [chartData, salePeriodTrendData]);

  /** Average units sold - must match summaryMetrics.avgUnits (card) so chart line and card agree */
  const avgUnitsSoldChart = useMemo(() => {
    if (!selectedFoodcourt || !selectedRestaurant || !selectedItem) return null;
    const [startDate, endDate] = dateRange || [];
    const activeDaySet = new Set(selectedDays.length ? selectedDays : DAY_NAME_OPTIONS);
    const filtered = (selectedItemDayRows || []).filter(row => {
      if (!startDate || !endDate) return true;
      const dateKey = parseCollatedDate((row.date || row.Date || '').toString().trim());
      const d = dateKey ? new Date(dateKey) : new Date(row.date);
      if (Number.isNaN(d.getTime())) return false;
      const dayName = d.toLocaleDateString('en-US', { weekday: 'long' });
      if (!activeDaySet.has(dayName)) return false;
      if (d < startDate || d > endDate) return false;
      return true;
    });
    let totalActual = 0;
    let countActual = 0;
    filtered.forEach(row => {
      const actual = toNumber(row.actual_qty);
      if (Number.isFinite(actual)) {
        totalActual += actual;
        if (actual > 0) countActual += 1;
      }
    });
    return countActual > 0 ? totalActual / countActual : null;
  }, [selectedItemDayRows, selectedFoodcourt, selectedRestaurant, selectedItem, dateRange, selectedDays]);

  const alignedChartData = useMemo(() => {
    const map = new Map(chartData.map((r) => [r.date, r]));
    const mgByDate = new Map(
      (mgData || []).map((r) => [String(r.date || '').slice(0, 10), parseFloat(r.quantity)])
    );
    return alignmentDates.map((date) => {
      const r = map.get(date);
      const inWeek = isInAnyForecastWeek(date, forecastWeekModelDates);
      let forecastVal = null;
      if (inWeek) {
        const rawForecast = r?.forecast ?? forecastByDate.get(date) ?? null;
        const parsedForecast = parseFloat(rawForecast);
        forecastVal = Number.isFinite(parsedForecast) && parsedForecast > 0 ? parsedForecast : 0;
      }
      const mgRaw = mgByDate.get(date);
      const base = {
        date,
        actual: r?.actual ?? null,
        forecast: forecastVal,
        description: r?.description || '',
        description_combo1: r?.description_combo1 || '',
        description_combo2: r?.description_combo2 || '',
        potential_reason: r?.potential_reason || '',
        mg_quantity: Number.isFinite(mgRaw) ? mgRaw : 0,
      };
      const withBand = attachBandFields(base, forecastBandByDate, r || null);
      const isHoliday = excludeHolidays && isDateHolidayDay(date);
      const actualNormal = isHoliday ? null : withBand.actual;
      const actualHoliday = isHoliday ? withBand.actual : null;
      return { ...withBand, avg_units_sold: avgUnitsSoldChart, isHoliday, actualNormal, actualHoliday };
    });
  }, [alignmentDates, chartData, avgUnitsSoldChart, excludeHolidays, forecastBandByDate, forecastByDate, forecastWeekModelDates, mgData]);

  const alignedSlotData = useMemo(() => {
    const map = new Map(salePeriodTrendData.map((r) => [r.date, r]));
    return alignmentDates.map((date) => {
      const r = map.get(date);
      const isHoliday = excludeHolidays && isDateHolidayDay(date);
      const base = r
        ? { ...r }
        : {
            date,
            offset: 0,
            span: 0,
            first_order_time: '',
            last_order_time: '',
            revenue_coverage: null,
            color: 'transparent'
          };
      const displayColor = isHoliday && base.color && base.color !== 'transparent' ? lightenHex(base.color, 0.4) : base.color;
      return { ...base, isHoliday, displayColor };
    });
  }, [alignmentDates, salePeriodTrendData, excludeHolidays]);

  /** Component Insights: all components with category + impact tag */
  const componentInsights = useMemo(() => {
    const rows = Array.isArray(friComponentStats) ? friComponentStats : [];
    const normalizedRows = rows
      .map((r) => {
        const ratio = parseFloat(r.avg_sale_ratio_1_over_0);
        return {
          ...r,
          component_name: (r.component_name || '').toString(),
          component_category: (r.component_category || 'Uncategorized').toString(),
          avg_sale_ratio_1_over_0: Number.isFinite(ratio) ? ratio : null,
          ratio_1_0_in_range: Number(r.ratio_1_0_in_range) || 0
        };
      })
      .filter((r) => r.component_name);

    const categoryOptions = [
      'All',
      ...Array.from(new Set(normalizedRows.map((r) => r.component_category).filter(Boolean))).sort((a, b) => a.localeCompare(b))
    ];

    const filteredRows = normalizedRows
      .filter((r) => selectedComponentCategory === 'All' || r.component_category === selectedComponentCategory)
      .map((r) => ({ ...r, impact_tag: getComponentImpactTag(r) }))
      .sort((a, b) => {
        const ratioA = Number.isFinite(a.avg_sale_ratio_1_over_0) ? a.avg_sale_ratio_1_over_0 : -Infinity;
        const ratioB = Number.isFinite(b.avg_sale_ratio_1_over_0) ? b.avg_sale_ratio_1_over_0 : -Infinity;
        return ratioB - ratioA;
      });

    return {
      categoryOptions,
      filteredRows
    };
  }, [friComponentStats, selectedComponentCategory]);

  const paginatedComponentRows = useMemo(() => {
    const start = componentPage * COMPONENTS_PAGE_SIZE;
    return componentInsights.filteredRows.slice(start, start + COMPONENTS_PAGE_SIZE);
  }, [componentInsights.filteredRows, componentPage]);

  const componentPageCount = useMemo(() => {
    return Math.max(1, Math.ceil(componentInsights.filteredRows.length / COMPONENTS_PAGE_SIZE));
  }, [componentInsights.filteredRows.length]);

  useEffect(() => {
    if (!componentInsights.filteredRows.length) {
      setSelectedComponentDetail(null);
      return;
    }
    const stillVisible = componentInsights.filteredRows.some(
      (r) =>
        selectedComponentDetail &&
        r.component_name === selectedComponentDetail.component_name &&
        r.component_category === selectedComponentDetail.component_category
    );
    if (!stillVisible) {
      setSelectedComponentDetail(componentInsights.filteredRows[0]);
    }
  }, [componentInsights]);

  useEffect(() => {
    setSelectedComponentCategory('All');
    setComponentPage(0);
  }, [selectedFoodcourt, selectedRestaurant, selectedItem, latestFolder]);

  useEffect(() => {
    setComponentPage(0);
  }, [selectedComponentCategory]);

  const summaryMetrics = useMemo(() => {
    if (!selectedFoodcourt || !selectedRestaurant || !selectedItem) {
      return null;
    }
    const [startDate, endDate] = dateRange || [];
    const activeDaySet = new Set(selectedDays.length ? selectedDays : DAY_NAME_OPTIONS);
    const filtered = (selectedItemDayRows || []).filter(row => {
      if (!startDate || !endDate) return true;
      const dateKey = parseCollatedDate((row.date || row.Date || '').toString().trim());
      const d = dateKey ? new Date(dateKey) : new Date(row.date);
      if (Number.isNaN(d.getTime())) return false;
      const dayName = d.toLocaleDateString('en-US', { weekday: 'long' });
      if (!activeDaySet.has(dayName)) return false;
      if (d < startDate || d > endDate) return false;
      return true;
    });
    
    if (filtered.length === 0) {
      return {
        daysForecasted: 0,
        avgUnits: null,
        avgAccuracy: null,
        avgError: null,
        inRangeHits: 0,
        inRangeTotal: 0,
        inRangePct: null,
        statusPct: { Green: 0, Orange: 0, Red: 0 }
      };
    }

    // Non Veg Lunch: use sum-grain metrics helper (weighted on summed actuals)
    if (isComboAggregateItem(selectedItem)) {
      const summary = summarizeComboAggregateMetrics(filtered);
      let daysForecasted = 0;
      const statusCounts = { Green: 0, Orange: 0, Red: 0 };
      let statusTotal = 0;
      filtered.forEach((row) => {
        const forecastVal = pickForecastValue(row);
        if (Number.isFinite(forecastVal)) daysForecasted += 1;
        const mape = pickMapeValue(row);
        const mad = pickMadValue(row);
        const status = calculateStatusFromMetrics(mape, mad);
        if (status) {
          statusCounts[status] = (statusCounts[status] || 0) + 1;
          statusTotal += 1;
        }
      });
      return {
        daysForecasted,
        avgUnits: summary.avg_daily_units_sold,
        avgAccuracy: summary.mape != null ? (1 - summary.mape) * 100 : null,
        avgError: summary.mad,
        inRangeHits: summary.inRangeHits ?? 0,
        inRangeTotal: summary.inRangeTotal ?? 0,
        inRangePct: summary.inRangePct,
        statusPct: {
          Green: statusTotal > 0 ? (statusCounts.Green / statusTotal) * 100 : 0,
          Orange: statusTotal > 0 ? (statusCounts.Orange / statusTotal) * 100 : 0,
          Red: statusTotal > 0 ? (statusCounts.Red / statusTotal) * 100 : 0,
        },
      };
    }
    
    let daysForecasted = 0;
    let totalActual = 0;
    let countActual = 0;
    let inRangeHits = 0;
    let inRangeTotal = 0;
    let weightedMapeSum = 0;
    let weightedMadSum = 0;
    let weightTotal = 0;
    const statusCounts = { Green: 0, Orange: 0, Red: 0 };
    let statusTotal = 0;
    
    filtered.forEach(row => {
      const forecastVal = pickForecastValue(row);
      if (Number.isFinite(forecastVal)) {
        daysForecasted += 1;
      }
      
      const actual = toNumber(row.actual_qty);
      if (Number.isFinite(actual)) {
        totalActual += actual;
        if (actual > 0) {
          countActual += 1;
          const dateKey = parseCollatedDate((row.date || row.Date || '').toString().trim());
          const band = dateKey ? forecastBandByDate.get(dateKey) : null;
          if (band && Number.isFinite(band.lower) && Number.isFinite(band.upper)) {
            inRangeTotal += 1;
            if (actual >= band.lower && actual <= band.upper) {
              inRangeHits += 1;
            }
          }
        }
      }
      
      const mape = pickMapeValue(row);
      const mad = pickMadValue(row);
      if (Number.isFinite(actual) && actual > 0) {
        if (Number.isFinite(mape)) {
          weightedMapeSum += mape * actual;
        }
        if (Number.isFinite(mad)) {
          weightedMadSum += mad * actual;
        }
        weightTotal += actual;
      }
      
      const status = calculateStatusFromMetrics(mape, mad);
      if (status) {
        statusCounts[status] = (statusCounts[status] || 0) + 1;
        statusTotal += 1;
      }
    });
    
    const avgUnits = countActual > 0 ? totalActual / countActual : null;
    const weightedMape = weightTotal > 0 ? weightedMapeSum / weightTotal : null;
    const weightedMad = weightTotal > 0 ? weightedMadSum / weightTotal : null;
    const avgAccuracy = weightedMape !== null ? (1 - weightedMape) * 100 : null;
    const statusPct = {
      Green: statusTotal > 0 ? (statusCounts.Green / statusTotal) * 100 : 0,
      Orange: statusTotal > 0 ? (statusCounts.Orange / statusTotal) * 100 : 0,
      Red: statusTotal > 0 ? (statusCounts.Red / statusTotal) * 100 : 0
    };
    
    return {
      daysForecasted,
      avgUnits,
      avgAccuracy,
      avgError: weightedMad,
      inRangeHits,
      inRangeTotal,
      inRangePct: inRangeTotal > 0 ? inRangeHits / inRangeTotal : null,
      statusPct
    };
  }, [selectedItemDayRows, selectedFoodcourt, selectedRestaurant, selectedItem, dateRange, selectedDays, forecastBandByDate]);

  if (!selectedRunDate) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">Select a run date to view item-level trends.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Card elevation={1} sx={{ mb: 3, bgcolor: 'primary.light', background: 'rgba(25, 118, 210, 0.08)' }}>
        <CardContent>
          {collatedError && <Alert severity="error" sx={{ mb: 2 }}>{collatedError}</Alert>}
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.75rem' }}>Foodcourt</InputLabel>
                <Select
                  value={selectedFoodcourt}
                  label="Foodcourt"
                  onChange={(e) => {
                    setSelectedFoodcourt(e.target.value);
                    setSelectedRestaurant('');
                    setSelectedItem('');
                  }}
                  disabled={loadingCollated || foodcourtOptions.length === 0}
                  sx={{
                    '& .MuiSelect-select': { fontSize: '0.75rem' }
                  }}
                >
                  {foodcourtOptions.map(option => (
                    <MenuItem key={option.id} value={option.id} sx={{ fontSize: '0.75rem' }}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.75rem' }}>Restaurant</InputLabel>
                <Select
                  value={selectedRestaurant}
                  label="Restaurant"
                  onChange={(e) => {
                    setSelectedRestaurant(e.target.value);
                    setSelectedItem('');
                  }}
                  disabled={!selectedFoodcourt || restaurantOptions.length === 0}
                  sx={{
                    '& .MuiSelect-select': { fontSize: '0.75rem' }
                  }}
                >
                  {restaurantOptions.map(option => (
                    <MenuItem key={option.id} value={option.id} sx={{ fontSize: '0.75rem' }}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.75rem' }}>Item</InputLabel>
                <Select
                  value={selectedItem}
                  label="Item"
                  onChange={(e) => setSelectedItem(e.target.value)}
                  disabled={!selectedRestaurant || itemOptions.length === 0}
                  sx={{
                    '& .MuiSelect-select': { fontSize: '0.75rem' }
                  }}
                >
                  {itemOptions.map(option => (
                    <MenuItem key={option.id} value={option.id} sx={{ fontSize: '0.75rem' }}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={excludeHolidays}
                    onChange={(e) => setExcludeHolidays(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                    Exclude Holiday Periods
                  </Typography>
                }
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      {summaryMetrics && (
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2} sx={{ flex: { md: '0 0 16.666%' }, maxWidth: { md: '16.666%' } }}>
              <Card
                elevation={1}
                sx={{
                  bgcolor: 'rgba(25, 118, 210, 0.08)',
                  borderLeft: '4px solid',
                  borderColor: 'primary.main',
                  height: 120,
                  minHeight: 120,
                  display: 'flex'
                }}
              >
                <CardContent sx={{ p: 1.5, display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    #Days Forecasted
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {summaryMetrics.daysForecasted ?? 'N/A'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2} sx={{ flex: { md: '0 0 16.666%' }, maxWidth: { md: '16.666%' } }}>
              <Card
                elevation={1}
                sx={{
                  bgcolor: 'rgba(76, 175, 80, 0.08)',
                  borderLeft: '4px solid',
                  borderColor: 'success.main',
                  height: 120,
                  minHeight: 120,
                  display: 'flex'
                }}
              >
                <CardContent sx={{ p: 1.5, display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Avg. Units Sold
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: 'success.main' }}>
                    {summaryMetrics.avgUnits !== null ? summaryMetrics.avgUnits.toFixed(0) : 'N/A'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2} sx={{ flex: { md: '0 0 16.666%' }, maxWidth: { md: '16.666%' } }}>
              <Card
                elevation={1}
                sx={{
                  bgcolor: 'rgba(255, 152, 0, 0.08)',
                  borderLeft: '4px solid',
                  borderColor: 'warning.main',
                  height: 120,
                  minHeight: 120,
                  display: 'flex'
                }}
              >
                <CardContent sx={{ p: 1.5, display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Avg. Accuracy (Weighted)
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: 'warning.main' }}>
                    {summaryMetrics.avgAccuracy !== null ? `${summaryMetrics.avgAccuracy.toFixed(1)}%` : 'N/A'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2} sx={{ flex: { md: '0 0 16.666%' }, maxWidth: { md: '16.666%' } }}>
              <Card
                elevation={1}
                sx={{
                  bgcolor: 'rgba(244, 67, 54, 0.08)',
                  borderLeft: '4px solid',
                  borderColor: 'error.main',
                  height: 120,
                  minHeight: 120,
                  display: 'flex'
                }}
              >
                <CardContent sx={{ p: 1.5, display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Avg. Error (Weighted)
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: 'error.main' }}>
                    {summaryMetrics.avgError !== null ? summaryMetrics.avgError.toFixed(1) : 'N/A'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2} sx={{ flex: { md: '0 0 16.666%' }, maxWidth: { md: '16.666%' } }}>
              <Card
                elevation={1}
                sx={{
                  bgcolor: 'rgba(255, 152, 0, 0.06)',
                  borderLeft: '4px solid',
                  borderColor: '#ef6c00',
                  height: 120,
                  minHeight: 120,
                  display: 'flex'
                }}
              >
                <CardContent sx={{ p: 1.5, display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Days In Range
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: '#ef6c00', lineHeight: 1.1 }}>
                    {summaryMetrics.inRangeTotal > 0
                      ? `${summaryMetrics.inRangeHits}/${summaryMetrics.inRangeTotal}`
                      : 'N/A'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                    {summaryMetrics.inRangePct != null
                      ? `${(summaryMetrics.inRangePct * 100).toFixed(0)}%`
                      : ''}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2} sx={{ flex: { md: '0 0 16.666%' }, maxWidth: { md: '16.666%' } }}>
              <Card
                elevation={1}
                sx={{
                  bgcolor: 'grey.50',
                  borderLeft: '4px solid',
                  borderColor: 'secondary.main',
                  height: 120,
                  minHeight: 120,
                  display: 'flex'
                }}
              >
                <CardContent sx={{ p: 1.5, display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
                  <Typography color="text.secondary" gutterBottom variant="body2" sx={{ fontWeight: 400 }}>
                    Distribution
                  </Typography>
                  <Grid container spacing={0} sx={{ mt: 0.5 }}>
                    <Grid item xs={4} sx={{ pr: 1 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography color="text.secondary" variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                          % Green
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 600, color: 'success.main', fontSize: '1.5rem', lineHeight: 1.1 }}>
                          {summaryMetrics.statusPct.Green.toFixed(1)}%
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4} sx={{ borderLeft: '1px solid', borderColor: 'grey.400', px: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography color="text.secondary" variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                          % Orange
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 600, color: 'warning.main', fontSize: '1.5rem', lineHeight: 1.1 }}>
                          {summaryMetrics.statusPct.Orange.toFixed(1)}%
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4} sx={{ borderLeft: '1px solid', borderColor: 'grey.400', pl: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography color="text.secondary" variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                          % Red
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 600, color: 'error.main', fontSize: '1.5rem', lineHeight: 1.1 }}>
                          {summaryMetrics.statusPct.Red.toFixed(1)}%
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}
      {loadingCollated && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {salesError && <Alert severity="error" sx={{ mb: 2 }}>{salesError}</Alert>}
      {loadingSales && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {!loadingSales && selectedItem && chartData.length === 0 && (
        <Alert severity="info">No chart data available for the selected item.</Alert>
      )}
      {!loadingSales && chartData.length > 0 && (
        <Card elevation={1}>
          <CardContent>
            <Box
              sx={{
                p: 1.5,
                mb: 2,
                bgcolor: 'rgba(76, 175, 80, 0.08)',
                borderBottom: '1px solid',
                borderColor: 'divider',
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 'calc(1.25rem - 2px)' }}>
                  Daily Level Accuracy
                </Typography>
                {(() => {
                  const labels = formatAggregatedPotentialReason(aggregatedReasonForChart);
                  return labels.length > 0 ? (
                    <Chip
                      label={labels[0]}
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 22 }}
                    />
                  ) : null;
                })()}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Time Period</InputLabel>
                  <Select
                    value={timePeriod}
                    label="Time Period"
                    onChange={(e) => setTimePeriod(e.target.value)}
                    sx={{ bgcolor: 'white' }}
                    renderValue={(selected) => {
                      const labels = {
                        '1month': '1 Month',
                        '3months': '3 Months',
                        '6months': '6 Months',
                        '1year': '1 Year',
                        '2years': '2 Years',
                        custom: 'Custom',
                      };
                      return labels[selected] || selected;
                    }}
                  >
                    {/* Keep value valid when custom without listing it as an option */}
                    {timePeriod === 'custom' && (
                      <MenuItem value="custom" sx={{ display: 'none' }} />
                    )}
                    <MenuItem value="1month">1 Month</MenuItem>
                    <MenuItem value="3months">3 Months</MenuItem>
                    <MenuItem value="6months">6 Months</MenuItem>
                    <MenuItem value="1year">1 Year</MenuItem>
                    <MenuItem value="2years">2 Years</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Days of Week</InputLabel>
                  <Select
                    multiple
                    value={selectedDays}
                    onChange={handleDayFilterChange}
                    label="Days of Week"
                    renderValue={(selected) => (
                      selected.length === DAY_NAME_OPTIONS.length
                        ? 'All Days'
                        : `${selected.length} Selected`
                    )}
                    sx={{ bgcolor: 'white' }}
                  >
                    <MenuItem key="select-all" value={ALL_DAYS_VALUE}>
                      <Checkbox checked={selectedDays.length === DAY_NAME_OPTIONS.length} />
                      <ListItemText primary="Select All" />
                    </MenuItem>
                    {DAY_NAME_OPTIONS.map((day) => (
                      <MenuItem key={day} value={day}>
                        <Checkbox checked={selectedDays.indexOf(day) > -1} />
                        <ListItemText primary={day} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {dataExtent.minDate && dataExtent.maxDate && dateRange[0] && dateRange[1] && !Number.isNaN(dateRange[0].getTime()) && !Number.isNaN(dateRange[1].getTime()) && (
                  <DateRangeFilter
                    dateRange={dateRange}
                    onChange={handleDateRangeChange}
                    minDate={dataExtent.minDate}
                    maxDate={dataExtent.maxDate}
                  />
                )}
              </Box>
            </Box>
            <Box sx={{ height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={alignedChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={CHART_TICK_STYLE}
                    angle={-45}
                    textAnchor="end"
                    height={70}
                    tickFormatter={formatDate}
                  />
                  <YAxis
                    label={{ value: 'Sales Quantity', angle: -90, position: 'insideLeft', style: CHART_TICK_STYLE }}
                    tick={CHART_TICK_STYLE}
                  />
                  <RechartsTooltip content={(props) => <CustomTooltip {...props} aggregatedReason={aggregatedReasonForChart} />} />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey={forecastBandRangeDataKey}
                    stroke="none"
                    fill={FORECAST_BAND_CHART.bandFill}
                    connectNulls={false}
                    isAnimationActive={false}
                    name="Forecast Range"
                    legendType="square"
                    tooltipType="none"
                  />
                  <Line
                    type="monotone"
                    dataKey={excludeHolidays ? 'actualNormal' : 'actual'}
                    stroke="#1976d2"
                    strokeWidth={2}
                    dot={false}
                    connectNulls={true}
                    name="Actual"
                    legendType="square"
                  />
                  {excludeHolidays && (
                    <Line
                      type="monotone"
                      dataKey="actualHoliday"
                      stroke="#1976d2"
                      strokeWidth={2}
                      strokeOpacity={0.35}
                      dot={false}
                      connectNulls={true}
                      legendType="none"
                    />
                  )}
                  <Line
                    type="monotone"
                    dataKey="forecast"
                    stroke="#ef5350"
                    strokeWidth={2}
                    strokeDasharray="8 4"
                    dot={false}
                    connectNulls={true}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    name="Forecast"
                    legendType="square"
                  />
                  {mgTypeForItem(selectedItem) && (
                    <Line
                      type="monotone"
                      dataKey="mg_quantity"
                      stroke="#8e24aa"
                      strokeWidth={2}
                      strokeDasharray="8 4"
                      dot={false}
                      connectNulls={true}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      name="MG"
                      legendType="line"
                    />
                  )}
                  {avgUnitsSoldChart != null && (
                    <Line
                      type="monotone"
                      dataKey="avg_units_sold"
                      stroke="#43a047"
                      strokeWidth={1.5}
                      strokeDasharray="4 4"
                      dot={false}
                      connectNulls={true}
                      name="Avg. Units Sold"
                      legendType="line"
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      )}
      {!loadingSales && salePeriodTrendData.length > 0 && (
        <Card elevation={1} sx={{ mt: 3 }}>
          <Box sx={{ p: 1, bgcolor: 'rgba(76, 175, 80, 0.08)', borderBottom: '1px solid', borderColor: 'divider', minHeight: '36px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 'calc(1.25rem - 2px)' }}>
              Session Slot-Wise Distribution
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem', mt: 0.25 }}>
              Primary Slot: {dominantSession && String(dominantSession).trim() ? toProperCase(String(dominantSession).trim()) : 'Not Defined'}
            </Typography>
          </Box>
          <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 0.5 } }}>
            <Box sx={{ height: 420 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={alignedSlotData}
                  margin={{ top: 2, right: 16, left: 16, bottom: 40 }}
                  barSize={24}
                  barCategoryGap="8%"
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    type="category"
                    dataKey="date"
                    tickFormatter={formatDate}
                    tick={CHART_TICK_STYLE}
                    angle={-45}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis
                    type="number"
                    domain={salePeriodYDomain}
                    allowDataOverflow
                    tickFormatter={(v) => {
                      const h = Math.floor(v / 60);
                      const m = v % 60;
                      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                    }}
                    tick={CHART_TICK_STYLE}
                    label={{ value: 'Time', angle: -90, position: 'insideLeft', style: CHART_TICK_STYLE }}
                  />
                  <RechartsTooltip
                    content={({ active, payload }) => {
                      if (!active || !payload || payload.length === 0) return null;
                      const p = payload[0]?.payload;
                      if (!p) return null;
                      return (
                        <div style={{
                          backgroundColor: 'rgba(255,255,255,0.95)',
                          border: '1px solid #ccc',
                          borderRadius: 4,
                          padding: 8,
                          fontSize: 12
                        }}>
                          <div style={{ fontWeight: 600, marginBottom: 4 }}>{formatDateWithWeekday(p.date)}</div>
                          <div>First: {p.first_order_time || '—'}</div>
                          <div>Last: {p.last_order_time || '—'}</div>
                          <div>Revenue Coverage: {p.revenue_coverage != null ? `${p.revenue_coverage.toFixed(1)}%` : '—'}</div>
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="offset" stackId="sp" fill="transparent" />
                  <Bar dataKey="span" stackId="sp" name="Sale period" radius={[4, 4, 0, 0]}>
                    {alignedSlotData.map((entry, index) => (
                      <Cell key={`${entry.date}-${index}`} fill={entry.displayColor ?? entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.2, justifyContent: 'center' }}>
              {[
                { label: 'Normal', color: '#66bb6a' },
                { label: 'Late Start', color: '#fb8c00' },
                { label: 'Early End', color: '#f48fb1' },
                { label: 'Late Start + Early End', color: '#e53935' }
              ].map((entry) => (
                <Box key={entry.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: entry.color, border: '1px solid rgba(0,0,0,0.2)' }} />
                  <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                    {entry.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}
      {/* Component Insights (fri-component stats from last model date) - same as ItemDetail */}
      {!isAggregateItem && (selectedFoodcourt && selectedRestaurant && selectedItem) && (
        <Card elevation={1} sx={{ mt: 3 }}>
          <Box sx={{ p: 1.5, bgcolor: 'rgba(76, 175, 80, 0.08)', borderBottom: '1px solid', borderColor: 'divider', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 'calc(1.25rem - 2px)' }}>
              Component Insights
            </Typography>
          </Box>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            {loadingFriComponentStats && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            )}
            {!loadingFriComponentStats && friComponentStatsError && (
              <Alert severity="error" sx={{ fontSize: '0.75rem' }}>
                {friComponentStatsError}
              </Alert>
            )}
            {!loadingFriComponentStats && !friComponentStatsError && (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                      <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel id="component-category-select-label" sx={{ fontSize: '0.75rem' }}>
                          Component Category
                        </InputLabel>
                        <Select
                          labelId="component-category-select-label"
                          value={selectedComponentCategory}
                          label="Component Category"
                          onChange={(e) => setSelectedComponentCategory(e.target.value)}
                          sx={{ fontSize: '0.75rem', '& .MuiSelect-select': { py: 0.75 } }}
                        >
                          {componentInsights.categoryOptions.map((category) => (
                            <MenuItem key={category} value={category} sx={{ fontSize: '0.75rem' }}>
                              {category}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    <Table size="small" sx={{ '& .MuiTableCell-root': { padding: '4px 8px', fontSize: '0.7rem' } }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Component</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Lift Factor</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Tag</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedComponentRows.length > 0 ? (
                          paginatedComponentRows.map((r) => (
                            <TableRow
                              key={`${r.component_name}-${r.component_category}`}
                              onClick={() => setSelectedComponentDetail(r)}
                              selected={selectedComponentDetail && selectedComponentDetail.component_name === r.component_name && selectedComponentDetail.component_category === r.component_category}
                              sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, '&.Mui-selected': { bgcolor: 'action.selected' } }}
                            >
                              <TableCell>{toProperCase(r.component_name)}</TableCell>
                              <TableCell align="right">
                                {Number.isFinite(Number(r.avg_sale_ratio_1_over_0))
                                  ? `${((Number(r.avg_sale_ratio_1_over_0) - 1) * 100).toFixed(0)}%`
                                  : '—'}
                              </TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={r.impact_tag}
                                  color={getImpactChipColor(r.impact_tag)}
                                  size="small"
                                  sx={{ fontSize: '0.65rem', height: 22 }}
                                />
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={3} sx={{ color: 'text.secondary' }}>
                              No components found for selected category
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                        Page {componentPage + 1} of {componentPageCount}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={componentPage === 0}
                          onClick={() => setComponentPage((prev) => Math.max(0, prev - 1))}
                          sx={{ minWidth: 72, fontSize: '0.7rem', py: 0.25 }}
                        >
                          Previous
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={componentPage >= componentPageCount - 1}
                          onClick={() => setComponentPage((prev) => Math.min(componentPageCount - 1, prev + 1))}
                          sx={{ minWidth: 56, fontSize: '0.7rem', py: 0.25 }}
                        >
                          Next
                        </Button>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, minHeight: 120 }}>
                    {selectedComponentDetail ? (
                      <>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>{toProperCase(selectedComponentDetail.component_name)}</Typography>
                        <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>Median units when component present vs absent</Typography>
                        <Box sx={{ height: 200, mb: 1.5 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={[
                                { name: 'When present', value: Number(selectedComponentDetail.avg_sale_flag_1) || 0, fill: (selectedComponentDetail.avg_sale_ratio_1_over_0 != null && selectedComponentDetail.avg_sale_ratio_1_over_0 < 1) ? '#e57373' : '#4caf50' },
                                { name: 'When absent', value: Number(selectedComponentDetail.avg_sale_flag_0) || 0, fill: '#9e9e9e' }
                              ]}
                              margin={{ top: 24, right: 16, left: 8, bottom: 24 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" vertical={false} />
                              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                              <YAxis tick={{ fontSize: 11 }} />
                              <Bar dataKey="value" name="Median units" radius={[4, 4, 0, 0]} label={{ position: 'top', fontSize: 11, formatter: (value) => (Number.isFinite(value) ? Math.round(value) : '') }} />
                            </BarChart>
                          </ResponsiveContainer>
                        </Box>
                        <Table size="small" sx={{ '& .MuiTableCell-root': { padding: '6px 10px', fontSize: '0.75rem' } }}>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Metric</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>Value</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell>Days Sold</TableCell>
                              <TableCell align="right">{selectedComponentDetail.days_with_flag_1 ?? '—'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Days Not Sold</TableCell>
                              <TableCell align="right">{selectedComponentDetail.days_with_flag_0 ?? '—'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Lift Factor</TableCell>
                              <TableCell align="right">
                                {(selectedComponentDetail.avg_sale_ratio_1_over_0 != null && Number.isFinite(selectedComponentDetail.avg_sale_ratio_1_over_0))
                                  ? `${((Number(selectedComponentDetail.avg_sale_ratio_1_over_0) - 1) * 100).toFixed(0)}%`
                                  : '—'}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </>
                    ) : (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>Select a component</Typography>
                    )}
                    </Box>
                  </Grid>
                </Grid>
                <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mt: 1.5, fontSize: '0.7rem' }}>
                  Click on Individual Components for Deep Dive
                </Typography>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

export default ItemAccuracyTrends;
